/** Settings (C0, design v2.1): a navigation column like System Settings, the section on the right;
 * on narrow screens the column becomes a scrolling capsule of links. */

import {
  Bell,
  Building2,
  CreditCard,
  FileSignature,
  FileText,
  Gem,
  type LucideIcon,
  Palette,
  PlugZap,
  ReceiptText,
  ShieldCheck,
  TrendingUp,
  UsersRound,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { useCanEditSettings } from "../lib/settings";
import { Page } from "./AppShell";

const SECTIONS: readonly { to: string; key: string; icon: LucideIcon; admin?: boolean }[] = [
  { to: "/settings/agency", key: "nav.agency", icon: Building2 },
  { to: "/settings/brand", key: "nav.brand", icon: Palette },
  { to: "/settings/documents", key: "nav.documents", icon: FileText },
  { to: "/settings/integrations", key: "nav.integrations", icon: PlugZap },
  // D-133: Management and Manager only (the API refuses the other roles too)
  { to: "/settings/payments", key: "nav.payments", icon: CreditCard, admin: true },
  { to: "/settings/team", key: "nav.team", icon: UsersRound },
  { to: "/settings/growth", key: "nav.growth", icon: TrendingUp },
  { to: "/settings/contracts", key: "nav.contractTemplates", icon: FileSignature },
  { to: "/settings/billing", key: "nav.billing", icon: ReceiptText },
  { to: "/settings/notifications", key: "notifications.nav", icon: Bell },
  { to: "/settings/audit", key: "nav.audit", icon: ShieldCheck },
  // D-147: the agency's plan, usage and AgencyOS invoices (owner, manager, accountant)
  { to: "/settings/subscription", key: "saas.nav", icon: Gem },
];

export function SettingsLayout({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  return (
    <Page title={title} description={intro}>
      <div className="settings-shell">
        <nav aria-label={t("nav.settingsNavigation")} className="settings-nav ao-glass">
          {SECTIONS.filter((section) => !section.admin || canEdit).map(({ to, key, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `settings-nav__link${isActive ? " active" : ""}`}>
              <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="settings-shell__content">
          {canEdit ? null : (
            <p role="note" className="muted">
              {t("common.readOnly")}
            </p>
          )}
          {children}
        </div>
      </div>
    </Page>
  );
}
